/**
 * PostMessage validation utilities that use FRAME_ANCESTORS environment variable
 * This ensures consistent security between CSP frame-ancestors and postMessage communication
 */

import * as Sentry from "@sentry/react";

/**
 * Get allowed origins from FRAME_ANCESTORS environment variable
 */
function getFrameAncestorsOrigins(): string[] {
  const allowedDomains = "https://*.blockdaemon.com https://blockdaemon.com http://localhost:*";

  const customDomains = import.meta.env.VITE_FRAME_ANCESTORS || "";

  const frameAncestors = customDomains
    ? `${allowedDomains} ${customDomains}`
    : `${allowedDomains} 'self'`;

  if (!frameAncestors) {
    return [];
  }

  return frameAncestors
    .split(/\s+/)
    .filter(Boolean)
    .filter((origin: string) => origin !== "self" && origin !== "none")
    .map((origin: string) => origin.trim());
}

/**
 * Check if an origin is allowed based on FRAME_ANCESTORS environment variable
 */
export function isOriginAllowed(origin: string): boolean {
  // Always allow same origin
  if (origin === window.location.origin) {
    return true;
  }

  // Check against FRAME_ANCESTORS origins
  const allowedOrigins = getFrameAncestorsOrigins();
  return allowedOrigins.some((allowedOrigin) => {
    // Handle wildcard subdomains (e.g., https://*.example.com)
    if (allowedOrigin.includes("*")) {
      const pattern = allowedOrigin.replace(/\./g, "\\.").replace(/\*/g, "[^.]+");
      const regex = new RegExp(`^${pattern}$`);
      return regex.test(origin);
    }

    // Exact match
    return origin === allowedOrigin;
  });
}

/**
 * Get allowed target origins for postMessage
 * Returns array of specific origins to use instead of "*"
 */
function getAllowedTargetOrigins(): string[] {
  const origins: string[] = [];

  // Add same origin
  origins.push(window.location.origin);

  // Add FRAME_ANCESTORS origins
  const frameAncestorsOrigins = getFrameAncestorsOrigins();
  frameAncestorsOrigins.forEach((origin) => {
    if (origin.includes("*")) {
      // For wildcards, we can't pre-expand them all, so we'll use "*" as fallback
      // This is still safer than unconditional "*" as we validate on receive
      origins.push("*");
    } else {
      origins.push(origin);
    }
  });

  return origins.length > 0 ? origins : ["*"];
}

/**
 * Validate incoming postMessage event origin
 */
export function validateMessageOrigin(event: MessageEvent): boolean {
  return isOriginAllowed(event.origin);
}

/**
 * Secure postMessage sender that uses validated target origins
 */
export function sendSecurePostMessage(
  targetWindow: Window,
  message: unknown,
  targetOrigins?: string[],
): void {
  const origins = targetOrigins || getAllowedTargetOrigins();

  // If we have specific origins, send to each one
  if (origins.length > 0 && !origins.includes("*")) {
    origins.forEach((origin) => {
      try {
        targetWindow.postMessage(message, origin);
      } catch (error) {
        // Silently continue if one origin fails
        console.debug("Failed to send message to origin:", origin, error);
      }
    });
  } else {
    // Fallback to wildcard (should be rare with proper configuration)
    targetWindow.postMessage(message, "*");
  }
}

/**
 * Higher-order function to create validated message listeners
 */
export function createSecureMessageListener<T = unknown>(
  handler: (data: T, event: MessageEvent) => void,
  options: {
    expectedTypes?: string[];
    validateData?: (data: unknown) => data is T;
  } = {},
): (event: MessageEvent) => void {
  return (event: MessageEvent) => {
    // Validate origin
    if (!validateMessageOrigin(event)) {
      console.warn("Rejected message from unauthorized origin:", event.origin);

      // Report unauthorized postMessage attempt to Sentry
      Sentry.withScope((scope) => {
        scope.setTag("security_event", "unauthorized_postmessage");
        scope.setTag("origin", event.origin);
        scope.setLevel("warning");
        scope.setContext("postMessage", {
          origin: event.origin,
          messageType: event.data?.type || "unknown",
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          location: window.location.href,
        });

        Sentry.captureMessage(`Unauthorized postMessage attempt from: ${event.origin}`, "warning");
      });

      return;
    }

    // Validate message structure
    if (!event.data || typeof event.data !== "object") {
      console.warn("Rejected malformed message:", event.data);
      return;
    }

    // Validate message type if specified
    if (options.expectedTypes && !options.expectedTypes.includes(event.data.type)) {
      console.warn("Rejected message with unexpected type:", event.data.type);
      return;
    }

    // Custom data validation if provided
    if (options.validateData && !options.validateData(event.data)) {
      console.warn("Rejected message that failed data validation:", event.data);
      return;
    }

    // Call the handler with validated data
    handler(event.data, event);
  };
}
